import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { IoClose } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { ActionIcon, Button, Checkbox, Modal, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import RequiredInput from '../../../../../components/forms/InputHOC';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import useTutors, { AssignableTutorsMeta } from '../../../../../hooks/api-calls/useTutors';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import FormGroup from '../../../../../layout/shared/form-group';
import { OrderService } from '../../../../../services/order/orderService';
import cn from '../../../../../utils/classNames';

interface ContractAssignmentModalProps {
    ids: string[];
    productIds: string[];
    isOpen: boolean;
    onClose: () => void;
}

const schema = yup.object({
    contractIds: yup.array().of(yup.string()).required('Este campo es obligatorio'),
    tutorId: yup.string().when('autoAssign', {
        is: (value: any) => (value === false),
        then: (schema) => schema.required('Este campo es obligatorio'),
        otherwise: (schema) => schema.notRequired(),
    }),
    autoAssign: yup.boolean().notRequired(),
    assignationDate: yup.string().nullable().notRequired(),
});

const formatProductsWithoutTutors = (products: AssignableTutorsMeta['productsWithoutTutors']) => {
    if (products.length === 0) {
        return '';
    }

    return products
        .map((product) => {
            const code = product.code ? `${product.code} - ` : '';
            return `${code}${product.name ?? 'Acción bonificada'}`;
        })
        .join(', ');
};

const getAssignableMessage = (
    meta: AssignableTutorsMeta,
    isAutoAssign: boolean,
    isLoading: boolean
): { text: string; tone: 'info' | 'warning' | 'error' } | null => {
    if (isLoading) {
        return null;
    }

    if (meta.reason === 'no_tutors_configured') {
        const productsLabel = formatProductsWithoutTutors(meta.productsWithoutTutors);
        const suffix = productsLabel ? `: ${productsLabel}` : '.';
        return {
            text: `La acción bonificada no dispone de tutores configurados${suffix}`,
            tone: 'error',
        };
    }

    if (meta.reason === 'no_available_on_date') {
        return {
            text: 'No hay tutores disponibles para la fecha seleccionada.',
            tone: 'warning',
        };
    }

    if (isAutoAssign) {
        return {
            text: 'Se asignará automáticamente al tutor con mayor prioridad disponible en la fecha seleccionada.',
            tone: 'info',
        };
    }

    return null;
};

const ContractAssignmentModal: React.FC<ContractAssignmentModalProps> = ({ ids, productIds, isOpen, onClose }) => {

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const {
        fetchAssignableTutors,
        getAssignableTutorsList,
        assignableMeta,
        isLoadingAssignable,
        hasAssignableTutors,
    } = useTutors({ order: id, products: productIds });
    const canSelectTutor = userCan('get_tutors_for_select', 'tutors');
    const previousAssignationDate = useRef<string>('');

    // STATES

    const [isLoading, setIsLoading] = React.useState(false);

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ENVÍA LA SOLICITUD DE ASIGNACIÓN DE CONTRATO
     * @EN SENDS THE CONTRACT ASSIGNMENT REQUEST
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const submit = async (values: any) => {
        setIsLoading(true);
        values.contractIds = ids;
        try {
            const response = await (new OrderService).assignToTutor(values);
            const responseData = response.getResponseData();

            if (responseData.success) {
                toast.success(responseData?.message || "Contrato asignado correctamente");
                formik.resetForm();
                onClose();
            } else {
                handleErrors(responseData);
            }
        } catch (error: any) {
            toast.error('Error al asignar el contrato');
        } finally {
            setIsLoading(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // FORMIK

    const formik = useFormik({
        initialValues: {
            contractIds: ids,
            tutorId: '',
            autoAssign: false,
            assignationDate: new Date().toISOString().split('T')[0],
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: values => { submit(values); },
    });

    const assignableMessage = useMemo(
        () => getAssignableMessage(assignableMeta, formik.values.autoAssign, isLoadingAssignable),
        [assignableMeta, formik.values.autoAssign, isLoadingAssignable]
    );

    const canSubmit = formik.values.autoAssign
        ? hasAssignableTutors && !isLoadingAssignable
        : canSelectTutor && hasAssignableTutors && !isLoadingAssignable;

    // USE EFFECT

    useEffect(() => {
        if (!isOpen || !formik.values.assignationDate) return;

        fetchAssignableTutors({
            order: id,
            products: productIds,
            contractIds: ids,
            assignationDate: formik.values.assignationDate,
        });
    }, [isOpen, formik.values.assignationDate, id, productIds, ids]);

    useEffect(() => {
        if (!isOpen) {
            previousAssignationDate.current = '';
            return;
        }

        if (
            previousAssignationDate.current &&
            previousAssignationDate.current !== formik.values.assignationDate
        ) {
            formik.setFieldValue('tutorId', '');
        }

        previousAssignationDate.current = formik.values.assignationDate;
    }, [formik.values.assignationDate, isOpen]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                onClose();
                formik.resetForm();
            }}
            size='sm'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-xl !shadow-xl'
            className='z-[999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">Asignar</Text>
                    <ActionIcon size='sm' variant='text' onClick={() => {
                        onClose();
                        formik.resetForm();
                    }}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <form onSubmit={formik.handleSubmit}>
                    <FormGroup
                        title="Asignar contrato"
                        description="Asigna un contrato a un tutor según su disponibilidad"
                        className="py-4"
                        titleCols="@md:col-span-4"
                        childCols="@md:col-span-8 md:grid-cols-2"
                    >
                        <Checkbox
                            id='autoAssign'
                            label={
                                <div className={'flex items-center'}>
                                    <label htmlFor='autoAssign'>Asignar automáticamente</label>
                                    <Tooltip
                                        size="md"
                                        content="El sistema asignará automáticamente un tutor disponible en la fecha seleccionada"
                                        placement="top"
                                        color="invert"
                                    >
                                        <div className={'inline-block ms-1'}>
                                            <HiOutlineInformationCircle size={20} color={'primary'} />
                                        </div>
                                    </Tooltip>
                                </div>
                            }
                            helperClassName="text-gray-500 text-sm mt-3 ms-8"
                            helperText={'El sistema asignará automáticamente un tutor disponible en la fecha seleccionada'}
                            checked={formik.values.autoAssign}
                            onChange={(e) => { formik.setValues({ ...formik.values, autoAssign: e.target.checked, tutorId: '' }) }}
                            error={formik.errors.autoAssign}
                            className='col-span-2'
                        />

                        <RequiredInput
                            id='assignationDate'
                            required={false}
                            type="date"
                            label="Fecha de asignación"
                            formik={formik}
                            className='col-span-2'
                            error={formik.errors.assignationDate}
                        />

                        {formik.values.autoAssign && assignableMessage && (
                            <div className={cn(
                                'col-span-2 flex items-center gap-2 text-sm',
                                {
                                    'text-gray-500': assignableMessage.tone === 'info',
                                    'text-amber-600': assignableMessage.tone === 'warning',
                                    'text-red-500': assignableMessage.tone === 'error',
                                }
                            )}>
                                <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                <span>{assignableMessage.text}</span>
                            </div>
                        )}

                        <div className={cn('col-span-2 space-y-2', { 'hidden': formik.values.autoAssign })}>
                            <CustomSelect
                                isSearchable
                                required
                                id={'tutorId'}
                                label="Tutor"
                                value={formik.values.tutorId ? getAssignableTutorsList().filter((u: any) => formik.values.tutorId.includes(u.value)) : []}
                                options={getAssignableTutorsList()}
                                onChange={(e: any) => { formik.setFieldValue('tutorId', e.value) }}
                                isDisabled={!canSelectTutor || isLoadingAssignable}
                                error={formik.errors.tutorId}
                            />
                            {isLoadingAssignable && (
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>Cargando tutores disponibles...</span>
                                </div>
                            )}
                            {canSelectTutor && !isLoadingAssignable && assignableMessage && (
                                <div className={cn(
                                    'flex items-center gap-2 text-sm',
                                    {
                                        'text-amber-600': assignableMessage.tone === 'warning',
                                        'text-red-500': assignableMessage.tone === 'error',
                                    }
                                )}>
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>{assignableMessage.text}</span>
                                </div>
                            )}
                            {!canSelectTutor && (
                                <div className="flex items-center gap-2 text-red-500 text-sm">
                                    <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                    <span>No tienes permisos para ver los tutores en los selectores.</span>
                                </div>
                            )}
                        </div>
                    </FormGroup>

                    <div className='flex justify-center p-7'>
                        <Button type='submit' size='md' isLoading={isLoading} disabled={isLoading || !canSubmit}>
                            Asignar
                        </Button>
                    </div>
                </form>

            </div>
        </Modal>
    );
};

export default ContractAssignmentModal;
